import { useState, useEffect, useCallback } from 'react';
import { useAPIClient, SubscriptionLimitError, SubscriptionAuthError, SubscriptionNotFoundError } from '../lib/api-client';

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  price_monthly: number;
  price_yearly: number;
  price_lifetime?: number; // For lifetime plans
  features: string[];
  max_todos: number | null;
  max_projects: number | null;
  max_monthly_cost: number;
  is_active: boolean;
  is_popular?: boolean;
  is_lifetime?: boolean; // Indicates if this is a lifetime plan
}

export interface UserSubscription {
  id: string;
  user_id: string;
  plan_id: string;
  status: 'active' | 'past_due' | 'cancelled' | 'paused' | 'unpaid' | 'incomplete';
  billing_cycle: 'monthly' | 'yearly' | 'lifetime';
  current_period_start: string;
  current_period_end: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  plan: SubscriptionPlan;
}

export interface UsageData {
  todos_created: number;
  projects_created: number;
  total_cost: number;
  usage_percentage: {
    todos: number | null;
    projects: number | null;
    cost: number;
  };
}

export interface UseSubscriptionResults {
  subscription: UserSubscription | null;
  plans: SubscriptionPlan[];
  usage: UsageData | null;
  loading: boolean;
  error: SubscriptionLimitError | SubscriptionAuthError | SubscriptionNotFoundError | Error | null;
  refreshSubscription: () => Promise<void>;
  createCheckoutSession: (planId: string, billingCycle: 'monthly' | 'yearly' | 'lifetime') => Promise<string>;
  createBillingPortalSession: () => Promise<string>;
  cancelSubscription: () => Promise<void>;
  checkUsageLimit: (action: 'create_todo' | 'create_project' | 'api_cost', amount?: number) => Promise<boolean>;
  createTodoWithCheck: (todo: any) => Promise<any>;
  createProjectWithCheck: (project: any) => Promise<any>;
  clearError: () => void;
}

export function useSubscription(): UseSubscriptionResults {
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<SubscriptionLimitError | SubscriptionAuthError | SubscriptionNotFoundError | Error | null>(null);

  const apiClient = useAPIClient();

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleApiError = (error: SubscriptionLimitError | SubscriptionAuthError | SubscriptionNotFoundError | Error) => {
    console.error('Subscription API error:', error);
    setError(error);
  };

  const fetchSubscription = useCallback(async () => {
    const result = await apiClient.getCurrentSubscription();
    
    if (result.success) {
      setSubscription(result.data);
    } else {
      // Only handle non-404 errors as actual errors
      if (!(result.error instanceof SubscriptionNotFoundError)) {
        handleApiError(result.error);
      } else {
        setSubscription(null);
      }
    }
  }, [apiClient]);

  const fetchPlans = useCallback(async () => {
    const result = await apiClient.getSubscriptionPlans();
    
    if (result.success) {
      setPlans(result.data);
    } else {
      handleApiError(result.error);
    }
  }, [apiClient]);

  const fetchUsage = useCallback(async () => {
    const result = await apiClient.getSubscriptionUsage();
    
    if (result.success) {
      setUsage(result.data);
    } else {
      handleApiError(result.error);
    }
  }, [apiClient]);

  const refreshSubscription = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      await Promise.all([
        fetchSubscription(),
        fetchPlans(),
        fetchUsage(),
      ]);
    } finally {
      setLoading(false);
    }
  }, [fetchSubscription, fetchPlans, fetchUsage]);

  const createCheckoutSession = useCallback(async (planId: string, billingCycle: 'monthly' | 'yearly' | 'lifetime'): Promise<string> => {
    const result = await apiClient.createCheckoutSession(planId, billingCycle);
    
    if (result.success) {
      return result.data.checkout_url;
    } else {
      handleApiError(result.error);
      throw result.error;
    }
  }, [apiClient]);

  const createBillingPortalSession = useCallback(async (): Promise<string> => {
    const result = await apiClient.createBillingPortalSession();
    
    if (result.success) {
      return result.data.billing_portal_url;
    } else {
      handleApiError(result.error);
      throw result.error;
    }
  }, [apiClient]);

  const cancelSubscription = useCallback(async () => {
    const result = await apiClient.cancelSubscription();
    
    if (result.success) {
      await refreshSubscription();
    } else {
      handleApiError(result.error);
      throw result.error;
    }
  }, [apiClient, refreshSubscription]);

  const checkUsageLimit = useCallback(async (action: 'create_todo' | 'create_project' | 'api_cost', amount: number = 1): Promise<boolean> => {
    const result = await apiClient.checkFeatureAccess(action, amount);
    
    if (result.success) {
      return result.data.allowed;
    } else {
      // For usage limit checks, we don't want to set a global error
      // Let the calling component handle the specific error
      console.warn('Usage limit check failed:', result.error);
      return false;
    }
  }, [apiClient]);

  const createTodoWithCheck = useCallback(async (todo: any) => {
    const result = await apiClient.createTodoWithCheck(todo);
    
    if (result.success) {
      // Refresh usage data after successful creation
      await fetchUsage();
      return result.data;
    } else {
      handleApiError(result.error);
      throw result.error;
    }
  }, [apiClient, fetchUsage]);

  const createProjectWithCheck = useCallback(async (project: any) => {
    const result = await apiClient.createProjectWithCheck(project);
    
    if (result.success) {
      // Refresh usage data after successful creation
      await fetchUsage();
      return result.data;
    } else {
      handleApiError(result.error);
      throw result.error;
    }
  }, [apiClient, fetchUsage]);

  // Initial load
  useEffect(() => {
    refreshSubscription();
  }, [refreshSubscription]);

  return {
    subscription,
    plans,
    usage,
    loading,
    error,
    refreshSubscription,
    createCheckoutSession,
    createBillingPortalSession,
    cancelSubscription,
    checkUsageLimit,
    createTodoWithCheck,
    createProjectWithCheck,
    clearError,
  };
}